import { API_URLS, CART_TYPE, API_BASE_PATH, RequestScope } from '@/constants/api.constants';
import type { MieleStoredGlobals, ILanguageObject } from '@/types/shared-types';
import type { CartsApiResponse, CartsCountApiResponse } from '@/types/cart-types';
import type { Customer } from '@/types/customer-types';

type CartHeaders = {
    apiKey: string;
    Authorization?: string;
};

export type MieleApiErrorResponse = {
    statusCode: number;
    statusMessage: string;
    stack: unknown[];
    data: string;
};

export interface Instance {
    getAnonymousCarts: (cartId: string) => Promise<CartsApiResponse>;
    getAnonymousCartsCount: (cartId: string) => Promise<CartsCountApiResponse>;
    getUserCartsCount: () => Promise<CartsCountApiResponse>;
    getUserCarts: () => Promise<CartsApiResponse>;
    getUserData: () => Promise<Customer>;
    getAlternateLanguages: (jsonPostBody: string) => Promise<ILanguageObject>;
}

export type MieleApiServiceInterface = { (arg: MieleStoredGlobals): Instance } & {
    new (arg: MieleStoredGlobals): Instance;
};

const buildRequestUrl = (globalsConfig: MieleStoredGlobals, url: string, params = {}, nonOCC?: boolean): string => {
    const ecomApiBasePath = API_BASE_PATH;
    const urlParams = new URLSearchParams({
        ...params,
        lang: `${globalsConfig['general.language']}_${globalsConfig['general.country'].toUpperCase()}`,
    }).toString();

    const joinedPath = nonOCC
        ? `${globalsConfig['general.experienceApi']}/${url}/${globalsConfig['module.eComApi.SiteId']}`
        : `${globalsConfig['general.experienceApi']}${ecomApiBasePath}${globalsConfig['module.eComApi.SiteId']}/${url}`;

    return `${joinedPath}?${urlParams}`;
};

function MieleApiService(globalsConfig: MieleStoredGlobals) {
    const defaultHeaders: CartHeaders = {
        apiKey: globalsConfig['module.eComApi.ApiKey'],
    };

    const get = async <T>(url: string, urlParams?: object, options?: object): Promise<T> => {
        const reqUrl = buildRequestUrl(globalsConfig, url, urlParams);
        const response = await fetch(reqUrl, Object.assign({ credentials: 'include' as RequestCredentials }, options));

        const data = await response.json();

        if (!response.ok) {
            throw new Error((data as MieleApiErrorResponse).data);
        }

        return data as T;
    };

    const post = async <T>(url: string, jsonPostBody: string, urlParams?: object, options?: object): Promise<T> => {
        const reqUrl = buildRequestUrl(globalsConfig, url, urlParams, true).replace(/([^:]\/)\/+/g, '$1'); // replace double-slash in path
        const response = await fetch(reqUrl, {
            method: 'POST',
            headers: Object.assign(
                { 'Content-Type': 'application/json', credentials: 'include' as RequestCredentials },
                options
            ),
            body: jsonPostBody,
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error((data as MieleApiErrorResponse).data);
        }

        return data as T;
    };

    const getAnonymousCarts = (cartId: string) =>
        get<CartsApiResponse>(
            `${API_URLS.ANON_CART}`,
            { cartGuid: cartId, fields: RequestScope.DEFAULT },
            {
                headers: {
                    ...defaultHeaders,
                },
            }
        );

    const getAnonymousCartsCount = (cartId: string) =>
        get<CartsCountApiResponse>(
            `${API_URLS.ANON_COUNT}`,
            { cartGuid: cartId, fields: RequestScope.BASIC },
            {
                headers: {
                    ...defaultHeaders,
                },
            }
        );

    const getUserCartsCount = () =>
        get<CartsCountApiResponse>(
            `${API_URLS.COUNT}`,
            { fields: RequestScope.BASIC, cartType: CART_TYPE },
            {
                headers: {
                    ...defaultHeaders,
                },
            }
        );

    const getUserCarts = () =>
        get<CartsApiResponse>(
            API_URLS.CARTS,
            {
                fields: RequestScope.DEFAULT,
                cartType: CART_TYPE,
            },
            {
                headers: {
                    ...defaultHeaders,
                },
            }
        );

    const getUserData = () =>
        get<Customer>(
            API_URLS.USER,
            {
                fields: RequestScope.FULL,
            },
            {
                headers: {
                    ...defaultHeaders,
                },
            }
        );

    const getAlternateLanguages = (jsonPostBody: string) => {
        return post<ILanguageObject>(
            API_URLS.ALTERNATE_LANGUAGES,
            jsonPostBody,
            {},
            {
                headers: {
                    ...defaultHeaders,
                },
            }
        );
    };

    return {
        getAnonymousCarts,
        getUserCarts,
        getUserData,
        getAnonymousCartsCount,
        getUserCartsCount,
        getAlternateLanguages,
    };
}

export default MieleApiService as MieleApiServiceInterface;
